// src/containers/reportBuilder/OutcomeRelationship.jsx
//
// Analysis of outcome relationship — landing page.
// Opened in a new tab from the OutcomeNetwork chart (Charts tab > Outcomes >
// Connections) when a highlighted link between two outcomes is clicked again.
//
// Ported from takso-poc/takso-reporting-ui/src/pages/OutcomeRelationship.jsx.
// This is intentionally a landing-page stub — the four analysis sections below
// are placeholders for future work, not yet built.
//
// URL params: o1, o2 (outcome ids), o1name, o2name, o1domain, o2domain, count

import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OUTCOME_ICONS } from '../../lib/outcomeIcons';
import { colorFor } from '../../theme/palette';
import './ReportBuilder.scss';

function OutcomeCard({ id, name, domain }) {
  const icon = OUTCOME_ICONS[id];
  const colour = colorFor(domain, 0);

  return (
    <div className="or-rel__card">
      <div className="or-rel__card-icon" style={{ background: colour }}>
        {icon ? (
          <img
            src={`/icons/outcomes/${icon}.png`}
            alt={name}
            className="or-rel__card-icon-img"
          />
        ) : (
          <span className="or-rel__card-icon-fallback">○</span>
        )}
      </div>
      <div className="or-rel__card-domain">{domain}</div>
      <div className="or-rel__card-name">{name}</div>
    </div>
  );
}

function PlaceholderSection({ title }) {
  const { t } = useTranslation();

  return (
    <div className="or-rel__placeholder">
      <div className="or-rel__placeholder-title">{title}</div>
      <div className="or-rel__placeholder-sub">
        {t('report.builder.infographic_coming_soon')}
      </div>
    </div>
  );
}

export default function OutcomeRelationship() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const o1 = Number(params.get('o1')) || 0;
  const o2 = Number(params.get('o2')) || 0;
  const o1name =
    params.get('o1name') || t('report.builder.outcome_relationship.outcome_a');
  const o2name =
    params.get('o2name') || t('report.builder.outcome_relationship.outcome_b');
  const o1domain = params.get('o1domain') || '';
  const o2domain = params.get('o2domain') || '';
  const count = Number(params.get('count')) || 0;

  return (
    <div className="or-rel">
      <div className="or-rel__inner">
        <div className="or-rel__header">
          <div className="or-rel__eyebrow">
            {t('report.builder.outcome_relationship.title')}
          </div>
          <h1 className="or-rel__title">
            {t('report.builder.outcome_relationship.subtitle')}
          </h1>
          <p className="or-rel__subtitle">
            {t('report.builder.outcome_relationship.description', { count })}
          </p>
        </div>

        <div className="or-rel__cards">
          <OutcomeCard id={o1} name={o1name} domain={o1domain} />
          <div className="or-rel__count-badge-wrap">
            <div className="or-rel__count-badge">{count}</div>
          </div>
          <OutcomeCard id={o2} name={o2name} domain={o2domain} />
        </div>

        <hr className="or-rel__divider" />

        <div className="or-rel__placeholders">
          <PlaceholderSection
            title={t(
              'report.builder.outcome_relationship.placeholder_section.activity_breakdown'
            )}
          />
          <PlaceholderSection
            title={t(
              'report.builder.outcome_relationship.placeholder_section.comparison_and_trend'
            )}
          />
          <PlaceholderSection
            title={t(
              'report.builder.outcome_relationship.placeholder_section.evaluation_rating_comparison'
            )}
          />
          <PlaceholderSection
            title={t(
              'report.builder.outcome_relationship.placeholder_section.timeline'
            )}
          />
        </div>
      </div>
    </div>
  );
}
